import React, { useMemo, useState, useEffect, useContext } from "react";
import { useParams } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import DataTable from "../../common/DataTable";
import { GET_EXPENSE_PARTY_LEDGER_REPORT_VIEW } from "../../../services/apiRoutes";
import { AuthContext } from "../../../context/AuthContext";
import CustomLoader from "../../common/CustomLoader";
import CustomReport from "../../common/CustomReport";
import { genericPost } from "../../../services/authServices";
import { Box, Card, CardContent, Typography, Container, Grid } from "@mui/material";
import FindReplaceOutlinedIcon from '@mui/icons-material/FindReplaceOutlined';
import { ClearRounded, } from "@mui/icons-material";
import CustomFilter from "../../common/CustomFilter";
import CustomButton from "../../common/CustomButton";
import CustomTextField from "../../common/CustomTextField";
import CustomDataTable from "../../common/CustomDataTable";

const title = "Expense Party Ledger Report";

const ExpensePartyLedgerReportView = () => {
    const { partyLedgerId } = useParams();
    const { t } = useTranslation();
    const [data, setData] = useState([]);
    const { token } = useContext(AuthContext);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [totalBillingAmount, setTotalBillingAmount] = useState(0);
    const [totalPaymentAmount, setTotalPaymentAmount] = useState(0);
    const [totalBalanceAmount, setTotalBalanceAmount] = useState(0);
    // 👉 New States to hold party name & currency symbol
    const [partyName, setPartyName] = useState("");
    const [currencySymbol, setCurrencySymbol] = useState("");
    const filterOpen = () => setOpenDrawer(true);
    const filterClose = () => setOpenDrawer(false);
    const [openDrawer, setOpenDrawer] = useState(false);
    const [page, setPage] = useState(0); // UI 0-based
    const [pageSize, setPageSize] = useState(10);
    const [rowCount, setRowCount] = useState(0);

    const [filterPayload, setFilterPayload] = useState({
        partyLedgerId: partyLedgerId,
        shipmentId: '',
    });
    const handleFilterChange = (field, value) => {
        setFilterPayload((prev) => ({
            ...prev,
            [field]: value || '',
        }));
    };
    const handleClearFilters = useMemo(() => {
        return () => {
            setFilterPayload({
                partyLedgerId: partyLedgerId,
                shipmentId: '',
            });
            fetchData(0, pageSize, '');
        };
    }, [pageSize]);

    const columns = useMemo(() => [
        { accessorKey: 'index', header: 'SN', size: 50, tableStyle: { textAlign: "center" } },
        { accessorKey: 'date', header: 'Date', size: 100, tableStyle: { textAlign: "center" } },
        { accessorKey: 'shipmentId', header: 'Shipment ID', size: 150, tableStyle: { textAlign: "center" } },
        { accessorKey: 'ledgerName', header: 'Ledger Name', size: 200, tableHeader: { justifyContent: 'flex-start' } },
        { accessorKey: 'currencySymbol', header: 'Currency', size: 100, tableStyle: { textAlign: "center" }, footer: 'Total', },
        {
            accessorKey: 'amountInBaseCurrency', header: 'Billing Amount', size: 180, tableStyle: { textAlign: "center" }, footer: `${currencySymbol} ${totalBillingAmount.toFixed(2)}`
        },

        {
            accessorKey: 'paymentAmount', header: 'Payment Amount', size: 180, tableStyle: { textAlign: "center" }, footerCellStyle: { textAlign: "center" }, footer: `${currencySymbol} ${totalPaymentAmount.toFixed(2)}`
        },

    ], [totalBillingAmount, totalPaymentAmount, totalBalanceAmount, currencySymbol]);

    const fetchData = async (partyLedgerId, pageIndex = 0, pageSize = 10) => {
        setLoading(true);
        try {
            const payload = {
                page: pageIndex + 1,
                limit: pageSize,
                ...filterPayload
            };
            const response = await genericPost(GET_EXPENSE_PARTY_LEDGER_REPORT_VIEW, payload);

            if (response.data.success && response.data.expensePartyLedgerSummary.length > 0) {
                const summary = response.data.expensePartyLedgerSummary;
                const firstItem = summary[0]; // 👈 Get first object in array

                // 👉 Set Party Name and Currency Symbol from first array item
                setPartyName(firstItem?.partyLedger?.partyName || "");
                setCurrencySymbol(firstItem?.baseCurrency?.currency_symbol || "");

                const totalBilling = response?.data?.totalBillingAmount || 0;
                const totalPayment = response?.data?.totalPaymentAmount || 0;
                const totalBalance = response?.data?.totalBalanceAmount || 0;

                const mappedData = response.data.expensePartyLedgerSummary.map((item, index) => ({
                    index: (index + 1).toString().padStart(2, '0'),
                    date: item.date || "",
                    shipmentId: item.shipmentId || "",
                    ledgerName: item.expenseLedger?.ledgerName || "",
                    currencySymbol: item.baseCurrency?.currency_name || "",
                    amountInBaseCurrency: `${item.baseCurrency?.currency_symbol || ""} ${item.amountInBaseCurrency || "0.00"}`,
                }));
                setData(mappedData);
                setTotalBillingAmount(totalBilling);
                setTotalPaymentAmount(totalPayment);
                setTotalBalanceAmount(totalBalance);
                setRowCount(response.data.pagination?.totalRecords || 0);

            } else {
                setData([]);
                setError("No data found.");
            }
        } catch (err) {
            console.error("Fetch Error:", err);
            setError(err.message);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(partyLedgerId, page, pageSize);
    }, [partyLedgerId, page, pageSize, token, filterPayload]);

    const defaultView = 'expensePartyLedgerReport';

    // 🎨 Define color theme for each card
    const cardStyles = {
        1: { backgroundColor: '#FFD7E9', color: '#41001d' },
        2: { backgroundColor: '#f0ecff', color: '#0e0047' },
        3: { backgroundColor: '#d4d0c4', color: '#464133' },
    };

    const cards = [
        { label: "Billing", currency_symbol: currencySymbol, amount: totalBillingAmount },
        { label: "Payment", currency_symbol: currencySymbol, amount: totalPaymentAmount },
        { label: "Balance", currency_symbol: currencySymbol, amount: totalBalanceAmount },
    ];


    const mainContent = (
        <>
            <CustomLoader open={loading} />

            <Container maxWidth="lg" sx={{ py: 1 }}>
                <Box
                    sx={{
                        display: "flex",
                        gap: 2,
                        flexWrap: "wrap",
                        justifyContent: "space-between",
                        alignItems: "stretch",
                        flexDirection: { xs: "column", sm: "row" },
                    }}
                >
                    {cards.map((card, idx) => {
                        const theme = cardStyles[idx + 1];
                        return (
                            <Card
                                key={idx}
                                variant="outlined"
                                sx={{
                                    backgroundColor: theme.backgroundColor,
                                    color: theme.color,
                                    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
                                    borderRadius: '10px',
                                    flex: "1 1 30%",
                                    minWidth: { xs: "100%", sm: "280px" },
                                    maxWidth: { sm: "32%" },
                                    transition: "transform 0.3s ease, box-shadow 0.3s ease",
                                    '&:hover': {
                                        transform: "translateY(-5px)",
                                        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
                                    },
                                    p: 3,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        // justifyContent: "space-between",
                                        justifyContent: "center",
                                        flexWrap: "wrap",
                                        width: "100%",
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }} style={{ fontSize: "20px" }}>
                                        {card.label}
                                    </Typography>
                                    <Typography
                                        style={{ fontSize: "20px" }}
                                        sx={{
                                            fontWeight: "bold",
                                            color: theme.color,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5,
                                        }}
                                    >
                                        <span  > - &nbsp; &nbsp; </span>
                                        <span>{card.currency_symbol}</span>
                                        <span>{card.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                    </Typography>
                                </Box>
                            </Card>
                        );
                    })}
                </Box>
            </Container>


            <CustomDataTable
                title={partyName}
                columns={columns}
                data={data}
                handleFilterButton={filterOpen}
                fiterButtonTitile="Fiter"
                rowCount={rowCount}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
            />

            <CustomFilter
                drawerTitle="Filter Options"
                drawerWidth={300}
                openDrawer={openDrawer}
                onClose={filterClose}
                drawerContent={
                    <>
                        <Box>
                            <Grid container spacing={1} sx={{ padding: 0, }}>
                                <Grid item xs={12} sm={12} md={12}>
                                    <CustomTextField
                                        type="text"
                                        placeholder="Enter Shipment ID"
                                        value={filterPayload.shipmentId}
                                        onChange={(e) => handleFilterChange('shipmentId', e.target.value)}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={12} md={12} sx={{ display: 'flex', alignItems: 'center', marginTop: "10px" }}>
                                    <CustomButton
                                        startIcon={<FindReplaceOutlinedIcon />}
                                        title="Apply"
                                        size="small"
                                        variant="contained"
                                        onClick={fetchData}
                                    />
                                    <CustomButton
                                        variant="outlined"
                                        title="Clear"
                                        startIcon={<ClearRounded />}
                                        onClick={handleClearFilters}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </>
                }
            />
        </>
    );

    return <CustomReport defaultView={defaultView} mainContent={mainContent} />;
};

export default ExpensePartyLedgerReportView;
